import { getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { companiesCollection, projectsCollection } from '@/config/collections';
import type { Business } from '@/types';
import type { BusinessView } from '@/contexts/BusinessContext';

export interface DashboardStats {
  totalCompanies: number;
  myProjects: number;
  closedThisQuarter: number;
  pendingCommission: number;
}

/**
 * Get dashboard statistics for an admin/owner.
 * Shows ALL companies and all projects for the current business view.
 * When businessView is 'both', combines stats from both businesses.
 */
export async function getAdminDashboardStats(businessView: BusinessView): Promise<DashboardStats> {
  try {
    // Total companies (companies are global, no business field)
    const companiesSnapshot = await getDocs(companiesCollection(db));
    const totalCompanies = companiesSnapshot.size;

    // All projects - either for specific business or all
    let projectsSnapshot;
    if (businessView === 'both') {
      // Get all projects (no business filter)
      projectsSnapshot = await getDocs(projectsCollection(db));
    } else {
      // Get projects for specific business
      const projectsQuery = query(
        projectsCollection(db),
        where('business', '==', businessView)
      );
      projectsSnapshot = await getDocs(projectsQuery);
    }
    const myProjects = projectsSnapshot.size;

    // Closed this quarter — key off closeDate presence, NOT status. Status is
    // stored as a display label ("10. Completed"), never the literal "closed",
    // so a status === 'closed' check would always be 0.
    const quarterStart = getQuarterStart();
    const closedThisQuarter = projectsSnapshot.docs.filter(doc => {
      const closeDate = doc.data().closeDate?.toDate?.() ?? null;
      return closeDate && closeDate >= quarterStart;
    }).length;

    // Pending commission across all projects for this business view
    const pendingCommission = projectsSnapshot.docs.reduce((sum, doc) => {
      const data = doc.data();
      if (data.commissionPaid === false && data.expectedCommission) {
        return sum + data.expectedCommission;
      }
      return sum;
    }, 0);

    return {
      totalCompanies,
      myProjects,
      closedThisQuarter,
      pendingCommission,
    };
  } catch (error) {
    console.error('Error fetching admin dashboard stats:', error);
    return {
      totalCompanies: 0,
      myProjects: 0,
      closedThisQuarter: 0,
      pendingCommission: 0,
    };
  }
}

/**
 * Get dashboard statistics for a sales rep.
 * Shows only companies/projects assigned to this rep.
 */
export async function getSalesRepDashboardStats(userId: string): Promise<DashboardStats> {
  try {
    // Total companies assigned to this sales rep
    const companiesQuery = query(
      companiesCollection(db),
      where('salesRepUserId', '==', userId)
    );
    const companiesSnapshot = await getDocs(companiesQuery);
    const totalCompanies = companiesSnapshot.size;

    // My projects (all projects assigned to this sales rep)
    const myProjectsQuery = query(
      projectsCollection(db),
      where('salesRepUserId', '==', userId)
    );
    const myProjectsSnapshot = await getDocs(myProjectsQuery);
    const myProjects = myProjectsSnapshot.size;

    // Closed this quarter
    const quarterStart = getQuarterStart();
    const closedQuery = query(
      projectsCollection(db),
      where('salesRepUserId', '==', userId),
      where('closeDate', '>=', Timestamp.fromDate(quarterStart))
    );
    const closedSnapshot = await getDocs(closedQuery);

    // Query already constrains closeDate >= quarterStart, which IS the
    // definition of "closed this quarter". Status is a display label, never
    // the literal "closed", so don't filter on it.
    const closedThisQuarter = closedSnapshot.size;

    // Pending commission (sum of expectedCommission where commissionPaid === false)
    const unpaidQuery = query(
      projectsCollection(db),
      where('salesRepUserId', '==', userId),
      where('commissionPaid', '==', false)
    );
    const unpaidSnapshot = await getDocs(unpaidQuery);
    const pendingCommission = unpaidSnapshot.docs.reduce((sum, doc) => {
      return sum + (doc.data().expectedCommission || 0);
    }, 0);

    return {
      totalCompanies,
      myProjects,
      closedThisQuarter,
      pendingCommission,
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    return {
      totalCompanies: 0,
      myProjects: 0,
      closedThisQuarter: 0,
      pendingCommission: 0,
    };
  }
}

/**
 * Get the start date of the current quarter
 */
function getQuarterStart(): Date {
  const now = new Date();
  const month = now.getMonth();
  const quarterMonth = Math.floor(month / 3) * 3; // 0, 3, 6, or 9
  return new Date(now.getFullYear(), quarterMonth, 1);
}
